import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import siteService from '../services/siteService';
import { useAuth } from './AuthContext';
import { getSiteFromHostname } from '../utils/site';

const SubdomainContext = createContext();

export const useSubdomain = () => {
  const context = useContext(SubdomainContext);
  if (!context) {
    throw new Error('useSubdomain debe ser usado dentro de un SubdomainProvider');
  }
  return context;
};

// Mantener compatibilidad con el nombre anterior
export const useSite = useSubdomain;

export const SubdomainProvider = ({ children }) => {
  const { user, hasRole, loading: authLoading } = useAuth();
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [locations, setLocations] = useState([]);
  const [activeLocation, setActiveLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [managerStatus, setManagerStatus] = useState(null);
  const loadingSitesRef = useRef(false);

  // Verificar si el usuario es manager (solo cuando el usuario esté disponible)
  const isManager = !authLoading && user ? hasRole('manager') : false;

  // Cargar estado del manager
  const loadManagerStatus = useCallback(async () => {
    if (!isManager || !user) return;

    try {
      setLoading(true);
      setError(null);
      const status = await siteService.checkManagerStatus();
      setManagerStatus(status);
    } catch (error) {
      console.error('Error cargando estado del manager:', error);
      
      // Si el error indica que el token está expirado, no mostrar error
      if (error.message.includes('expirado') || error.message.includes('expired') || error.message.includes('401')) {
        console.warn('SubdomainContext: Token appears to be expired, not showing error');
        return;
      }
      
      // Si es un error 500, mostrar un mensaje más específico
      if (error.message.includes('500')) {
        setError('Error del servidor. Por favor, intenta nuevamente o contacta al administrador.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [isManager, user]);

  // Cargar sites del manager
  const loadSites = useCallback(async (showLoading = true, forceReload = false) => {
    if (!isManager || !user || loadingSitesRef.current) return; // Evitar cargas múltiples simultáneas
    
    // Si ya hay sites cargados y no se fuerza la recarga, no hacer nada
    if (!forceReload && sites.length > 0) {
      return;
    }

    try {
      loadingSitesRef.current = true;
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const response = await siteService.getMySites();
      const sitesData = response.sites || [];
      
      // Cargar ubicaciones para cada site
      const sitesWithLocations = await Promise.all(
        sitesData.map(async (site) => {
          try {
            console.log(`🔍 Cargando ubicaciones para site ${site.id} (${site.name})`);
            const locationsResponse = await siteService.getLocationsBySite(site.id);
            const locations = locationsResponse.locations || [];
            console.log(`✅ Site ${site.id} tiene ${locations.length} ubicaciones:`, locations.map(l => ({ id: l.id, name: l.name })));
            return {
              ...site,
              locations: locations
            };
          } catch (error) {
            console.warn(`❌ Error cargando ubicaciones para site ${site.id}:`, error);
            return {
              ...site,
              locations: []
            };
          }
        })
      );
      
      setSites(sitesWithLocations);
      
      // Si no hay site activo y hay sites disponibles, seleccionar el primero
      if (!activeSite && sitesWithLocations.length > 0) {
        setActiveSite(sitesWithLocations[0]);
      }
    } catch (error) {
      console.error('Error cargando sites:', error);
      setError(error.message);
    } finally {
      loadingSitesRef.current = false;
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [isManager, user, activeSite, sites.length]);

  // Mantener compatibilidad con el nombre anterior
  const loadSubdomains = loadSites;

  // Crear un nuevo site
  const createSite = async (siteData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validar datos
      const validation = siteService.validateSiteData(siteData);
      if (!validation.isValid) {
        throw new Error('Datos inválidos: ' + Object.values(validation.errors).join(', '));
      }

      // Formatear datos
      const formattedData = siteService.formatSiteDataForSubmission(siteData);
      
      // Crear site
      const response = await siteService.createSite(formattedData);
      
      // Agregar el nuevo site con ubicaciones vacías a la lista actual
      const newSite = {
        ...response.site,
        locations: []
      };
      setSites(prevSites => [...prevSites, newSite]);
      
      // Recargar estado del manager
      await loadManagerStatus();
      
      return response;
    } catch (error) {
      console.error('Error creando site:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Mantener compatibilidad con el nombre anterior
  const createSubdomain = createSite;

  // Actualizar un site
  const updateSite = async (siteId, siteData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validar datos
      const validation = siteService.validateSiteData(siteData);
      if (!validation.isValid) {
        throw new Error('Datos inválidos: ' + Object.values(validation.errors).join(', '));
      }

      // Formatear datos
      const formattedData = siteService.formatSiteDataForSubmission(siteData);
      
      // Actualizar site
      const response = await siteService.updateSite(siteId, formattedData);
      
      // Actualizar el site en la lista actual manteniendo las ubicaciones existentes
      setSites(prevSites => 
        prevSites.map(site => 
          site.id === siteId 
            ? { ...response.site, locations: site.locations || [] }
            : site
        )
      );
      
      // Recargar estado del manager
      await loadManagerStatus();
      
      return response;
    } catch (error) {
      console.error('Error actualizando site:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Mantener compatibilidad con el nombre anterior
  const updateSubdomain = updateSite;

  // Obtener un site específico
  const getSite = async (siteId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await siteService.getSite(siteId);
      return response.site;
    } catch (error) {
      console.error('Error obteniendo site:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Mantener compatibilidad con el nombre anterior
  const getSubdomain = getSite;

  // Cambiar site activo
  const changeActiveSite = (site) => {
    const currentSubdomain = getCurrentSubdomain();
    
    console.log('🔄 === CAMBIO DE SITE ACTIVO ===');
    console.log('🏢 Nuevo site:', { id: site.id, name: site.name, subdomain: site.subdomain });
    console.log('🌐 Subdominio actual:', currentSubdomain);
    
    // Validar consistencia de subdominio
    if (currentSubdomain !== 'default') {
      if (site.subdomain !== currentSubdomain) {
        console.log('⚠️ DISCREPANCIA: El site seleccionado no coincide con el subdominio actual');
        console.log('🚀 REDIRIGIENDO A subdominio correcto...');
        
        // Redirigir al subdominio correcto del site
        const redirectUrl = `https://${site.subdomain}.olimpo.club${window.location.pathname}${window.location.search}${window.location.hash}`;
        console.log('🎯 URL de redirección:', redirectUrl);
        window.location.href = redirectUrl;
        return;
      } else {
        console.log('✅ Site seleccionado coincide con subdominio actual');
      }
    } else {
      console.log('✅ Subdominio "default" - permitiendo cambio de site');
    }
    
    setActiveSite(site);
    // Guardar en localStorage para persistencia
    localStorage.setItem('active_site_id', site.id);
    console.log('🔄 === CAMBIO DE SITE ACTIVO COMPLETADO ===');
  };

  // Mantener compatibilidad con el nombre anterior
  const changeActiveSubdomain = changeActiveSite;

  // Obtener sites padre (para crear sites hijos)
  const getParentSites = () => {
    return sites.filter(site => !site.parent_site_id);
  };

  // Obtener sites hijos de un site específico
  const getChildSites = (parentSiteId) => {
    return sites.filter(site => site.parent_site_id === parentSiteId);
  };

  // Mantener compatibilidad con el nombre anterior
  const getParentSubdomains = getParentSites;
  const getChildSubdomains = getChildSites;

  // ===== MÉTODOS PARA LOCATIONS =====

  // Cargar locations de un site específico
  const loadLocationsBySite = useCallback(async (siteId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await siteService.getLocationsBySite(siteId);
      const locationsData = response.locations || [];
      
      // Actualizar las ubicaciones del site específico en la lista de sites
      setSites(prevSites => 
        prevSites.map(site => 
          site.id === siteId 
            ? { ...site, locations: locationsData }
            : site
        )
      );
      
      // También actualizar el estado global de locations para compatibilidad
      setLocations(locationsData);
      
      return locationsData;
    } catch (error) {
      console.error('Error cargando locations:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mantener compatibilidad con el nombre anterior
  const loadLocationsBySubdomain = loadLocationsBySite;

  // Crear una nueva location
  const createLocation = useCallback(async (locationData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validar que site_id esté presente y sea válido
      if (!locationData.site_id || locationData.site_id.trim() === '') {
        throw new Error('Site ID es requerido y debe ser válido');
      }
      
      // Validar datos
      const validation = siteService.validateLocationData(locationData);
      if (!validation.isValid) {
        throw new Error('Datos inválidos: ' + Object.values(validation.errors).join(', '));
      }

      // Formatear datos
      const formattedData = siteService.formatLocationDataForSubmission(locationData);
      
      // Crear location
      const response = await siteService.createLocation(formattedData);
      
      // Recargar locations del site
      await loadLocationsBySite(locationData.site_id);
      
      // Actualizar la lista de sites con la nueva ubicación
      setSites(prevSites => 
        prevSites.map(site => 
          site.id === locationData.site_id 
            ? { ...site, locations: [...(site.locations || []), response.location] }
            : site
        )
      );
      
      return response;
    } catch (error) {
      console.error('Error creando location:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadLocationsBySite]);

  // Actualizar una location
  const updateLocation = useCallback(async (locationId, locationData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validar datos
      const validation = siteService.validateLocationData(locationData);
      if (!validation.isValid) {
        throw new Error('Datos inválidos: ' + Object.values(validation.errors).join(', '));
      }

      // Formatear datos
      const formattedData = siteService.formatLocationDataForSubmission(locationData);
      
      // Actualizar location
      const response = await siteService.updateLocation(locationId, formattedData);
      
      // Recargar locations del site
      await loadLocationsBySite(locationData.site_id);
      
      // Actualizar la lista de sites con la ubicación actualizada
      setSites(prevSites => 
        prevSites.map(site => 
          site.id === locationData.site_id 
            ? { 
                ...site, 
                locations: site.locations?.map(loc => 
                  loc.id === locationId ? response.location : loc
                ) || []
              }
            : site
        )
      );
      
      return response;
    } catch (error) {
      console.error('Error actualizando location:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadLocationsBySite]);

  // Obtener una location específica
  const getLocation = useCallback(async (locationId, siteId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await siteService.getLocation(locationId, siteId);
      return response.location;
    } catch (error) {
      console.error('Error obteniendo location:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);


  // Eliminar una location
  const deleteLocation = useCallback(async (locationId, siteId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Eliminar location
      await siteService.deleteLocation(locationId, siteId);
      
      // Recargar locations del site
      await loadLocationsBySite(siteId);
      
      // Actualizar la lista de sites removiendo la ubicación eliminada
      setSites(prevSites => 
        prevSites.map(site => 
          site.id === siteId 
            ? { 
                ...site, 
                locations: site.locations?.filter(loc => loc.id !== locationId) || []
              }
            : site
        )
      );
      
      return true;
    } catch (error) {
      console.error('Error eliminando location:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadLocationsBySite]);

  // Cambiar location activa
  const changeActiveLocation = (location) => {
    setActiveLocation(location);
    // Guardar en localStorage para persistencia
    localStorage.setItem('active_location_id', location.id);
  };

  // Obtener ubicaciones de un site específico desde la lista de sites
  const getLocationsBySite = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    const locations = site?.locations || [];
    console.log(`📍 Obteniendo ubicaciones para site ${siteId}:`, {
      siteFound: !!site,
      siteName: site?.name,
      locationsCount: locations.length,
      locations: locations.map(l => ({ id: l.id, name: l.name }))
    });
    return locations;
  };

  // Verificar si necesita crear un site
  const needsSiteCreation = () => {
    return isManager && managerStatus && managerStatus.needs_site_creation;
  };

  // Mantener compatibilidad con el nombre anterior
  // const needsSubdomainCreation = needsSiteCreation; // No se usa actualmente

  // ===== MÉTODOS PARA SUBDOMAIN =====

  // Obtener el subdomain actual desde el hostname
  const getCurrentSubdomain = () => {
    const hostname = window.location.hostname;
    const detectedSubdomain = getSiteFromHostname(hostname);
    const finalSubdomain = detectedSubdomain || 'default';
    
    console.log('🌐 === DETECCIÓN DE SUBDOMINIO ===');
    console.log('📍 Hostname:', hostname);
    console.log('🔍 Detected from hostname:', detectedSubdomain);
    console.log('🎯 Final subdomain:', finalSubdomain);
    console.log('📊 Analysis:', {
      isLocalhost: hostname === 'localhost',
      isIP: /^\d+\.\d+\.\d+\.\d+$/.test(hostname),
      hasSubdomain: detectedSubdomain !== null,
      willUseDefault: detectedSubdomain === null
    });
    console.log('🌐 === DETECCIÓN COMPLETADA ===');
    
    // Si no se detecta subdominio (localhost, 127.0.0.1, etc.), usar "default"
    return finalSubdomain;
  };

  // Verificar si el site actual tiene subdomain configurado
  const hasSubdomainConfigured = (site) => {
    return site && site.subdomain && site.subdomain.trim() !== '';
  };

  // Verificar si el subdomain actual coincide con el site seleccionado
  const isSubdomainMatching = (site) => {
    const currentSubdomain = getCurrentSubdomain();
    const matchResult = currentSubdomain === site?.subdomain;
    
    console.log('🔍 Comparación detallada de subdominios:', {
      currentSubdomain,
      siteSubdomain: site?.subdomain,
      currentSubdomainType: typeof currentSubdomain,
      siteSubdomainType: typeof site?.subdomain,
      currentSubdomainLength: currentSubdomain?.length,
      siteSubdomainLength: site?.subdomain?.length,
      exactMatch: currentSubdomain === site?.subdomain,
      result: matchResult
    });
    
    return matchResult;
  };

  // Verificar si debe redirigir a subdomain
  const shouldRedirectToSubdomain = (site) => {
    return hasSubdomainConfigured(site) && !isSubdomainMatching(site);
  };

  // Construir URL de redirección a subdomain
  const buildSubdomainUrl = (site) => {
    if (!hasSubdomainConfigured(site)) return null;
    
    const currentUrl = new URL(window.location.href);
    const protocol = currentUrl.protocol;
    const port = currentUrl.port ? `:${currentUrl.port}` : '';
    
    // Construir el nuevo hostname con el subdomain del site
    const domainParts = currentUrl.hostname.split('.');
    if (domainParts.length >= 2) {
      // Reemplazar el primer elemento (subdomain actual) con el del site
      domainParts[0] = site.subdomain;
      const newHostname = domainParts.join('.');
      return `${protocol}//${newHostname}${port}${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    }
    
    // Fallback: usar el subdomain del site como subdomain
    return `${protocol}//${site.subdomain}.${currentUrl.hostname}${port}${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  };

  // Ejecutar redirección a subdomain si es necesaria
  const executeSubdomainRedirect = (site) => {
    if (shouldRedirectToSubdomain(site)) {
      const redirectUrl = buildSubdomainUrl(site);
      if (redirectUrl) {
        console.log('Redirecting to subdomain:', redirectUrl);
        window.location.href = redirectUrl;
        return true;
      }
    }
    return false;
  };

  // Verificar acceso basado en subdomain
  const validateSubdomainAccess = (site) => {
    const currentSubdomain = getCurrentSubdomain();
    
    console.log('🔍 === VALIDACIÓN DE SUBDOMINIO INICIADA ===');
    console.log('📍 URL Info:', {
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      fullUrl: window.location.href
    });
    console.log('🏢 Site Info:', {
      siteId: site?.id,
      siteName: site?.name,
      siteSubdomain: site?.subdomain,
      siteSubdomainConfigured: hasSubdomainConfigured(site)
    });
    console.log('🌐 Subdomain Info:', {
      currentSubdomain,
      detectedFromHostname: getSiteFromHostname(window.location.hostname),
      isDefault: currentSubdomain === 'default'
    });
    
    // BYPASS: Si el subdomain actual es "default", permitir acceso siempre
    if (currentSubdomain === 'default') {
      console.log('✅ BYPASS ACTIVADO: Default subdomain - bypassing validation');
      console.log('🎯 RESULTADO: Acceso permitido (default subdomain)');
      return { hasAccess: true, reason: 'Default subdomain - bypassed validation' };
    }
    
    // Si no hay subdomain en la URL, permitir acceso
    if (!currentSubdomain) {
      console.log('✅ BYPASS ACTIVADO: No subdomain detected in URL');
      console.log('🎯 RESULTADO: Acceso permitido (no subdomain)');
      return { hasAccess: true, reason: 'No subdomain in URL' };
    }
    
    // Si el site no tiene subdomain configurado, denegar acceso
    if (!hasSubdomainConfigured(site)) {
      console.log('❌ VALIDACIÓN FALLIDA: Site has no subdomain configured');
      console.log('🎯 RESULTADO: Acceso denegado (no subdomain configured)');
      return { hasAccess: false, reason: 'Site has no subdomain configured' };
    }
    
    // Verificar si el subdomain coincide
    const isMatching = isSubdomainMatching(site);
    console.log('🔍 Comparación de subdominios:', {
      currentSubdomain,
      siteSubdomain: site.subdomain,
      areEqual: currentSubdomain === site.subdomain,
      isMatchingResult: isMatching
    });
    
    // Si el subdomain no coincide, denegar acceso
    if (!isMatching) {
      console.log('❌ VALIDACIÓN FALLIDA: Subdomain mismatch');
      console.log('🎯 RESULTADO: Acceso denegado (subdomain mismatch)');
      return { hasAccess: false, reason: 'Subdomain mismatch', currentSubdomain, siteSubdomain: site.subdomain };
    }
    
    console.log('✅ VALIDACIÓN EXITOSA: Subdomain matches perfectly');
    console.log('🎯 RESULTADO: Acceso permitido (subdomain matches)');
    console.log('🔍 === VALIDACIÓN DE SUBDOMINIO COMPLETADA ===');
    return { hasAccess: true, reason: 'Subdomain matches' };
  };

  // Cargar site activo desde localStorage y validar consistencia de subdominio
  useEffect(() => {
    if (sites.length > 0) {
      const currentSubdomain = getCurrentSubdomain();
      const savedActiveSiteId = localStorage.getItem('active_site_id');
      
      console.log('🏠 === CARGA DE SITE ACTIVO ===');
      console.log('📋 Sites disponibles:', sites.map(s => ({ id: s.id, name: s.name, subdomain: s.subdomain })));
      console.log('🌐 Subdominio actual:', currentSubdomain);
      console.log('💾 Site guardado en localStorage:', savedActiveSiteId);
      
      let selectedSite = null;
      
      if (savedActiveSiteId) {
        const savedSite = sites.find(site => site.id === savedActiveSiteId);
        if (savedSite) {
          console.log('🔍 Site encontrado en localStorage:', { id: savedSite.id, name: savedSite.name, subdomain: savedSite.subdomain });
          
          // Validar consistencia de subdominio
          if (currentSubdomain !== 'default') {
            if (savedSite.subdomain !== currentSubdomain) {
              console.log('⚠️ DISCREPANCIA DETECTADA: Site seleccionado no coincide con subdominio actual');
              console.log('🔄 Buscando site con subdominio coincidente...');
              
              // Buscar site con subdominio coincidente
              const matchingSite = sites.find(site => site.subdomain === currentSubdomain);
              if (matchingSite) {
                console.log('✅ Site coincidente encontrado:', { id: matchingSite.id, name: matchingSite.name, subdomain: matchingSite.subdomain });
                selectedSite = matchingSite;
                
                // Redirigir al subdominio correcto
                const redirectUrl = `https://${currentSubdomain}.olimpo.club${window.location.pathname}${window.location.search}${window.location.hash}`;
                console.log('🚀 REDIRIGIENDO A:', redirectUrl);
                window.location.href = redirectUrl;
                return;
              } else {
                console.log('❌ No se encontró site con subdominio coincidente');
              }
            } else {
              console.log('✅ Site seleccionado coincide con subdominio actual');
              selectedSite = savedSite;
            }
          } else {
            console.log('✅ Subdominio "default" - permitiendo cualquier site');
            selectedSite = savedSite;
          }
        } else {
          console.log('❌ Site guardado no encontrado en la lista actual');
          selectedSite = sites[0];
        }
      } else {
        console.log('📝 No hay site guardado, usando el primero disponible');
        selectedSite = sites[0];
      }
      
      if (selectedSite) {
        console.log('🎯 Site seleccionado:', { id: selectedSite.id, name: selectedSite.name, subdomain: selectedSite.subdomain });
        setActiveSite(selectedSite);
      }
      
      console.log('🏠 === CARGA DE SITE ACTIVO COMPLETADA ===');
    }
  }, [sites]);

  // Validar consistencia de subdominio cuando cambia el site activo (incluso en página principal)
  useEffect(() => {
    if (activeSite && !authLoading) {
      const currentSubdomain = getCurrentSubdomain();
      
      console.log('🔄 === VALIDACIÓN DE CONSISTENCIA DEL SITE ACTIVO ===');
      console.log('🏢 Site activo:', { id: activeSite.id, name: activeSite.name, subdomain: activeSite.subdomain });
      console.log('🌐 Subdominio actual:', currentSubdomain);
      
      // Solo validar si no estamos en subdominio "default"
      if (currentSubdomain !== 'default') {
        if (activeSite.subdomain !== currentSubdomain) {
          console.log('⚠️ DISCREPANCIA DETECTADA EN SITE ACTIVO');
          console.log('🚀 REDIRIGIENDO A subdominio correcto...');
          
          // Redirigir al subdominio correcto del site activo
          const redirectUrl = `https://${activeSite.subdomain}.olimpo.club${window.location.pathname}${window.location.search}${window.location.hash}`;
          console.log('🎯 URL de redirección:', redirectUrl);
          window.location.href = redirectUrl;
          return;
        } else {
          console.log('✅ Site activo coincide con subdominio actual');
        }
      } else {
        console.log('✅ Subdominio "default" - no se requiere validación');
      }
      
      console.log('🔄 === VALIDACIÓN DE CONSISTENCIA COMPLETADA ===');
    }
  }, [activeSite, authLoading]);

  // Cargar estado del manager cuando el usuario es manager y está autenticado
  useEffect(() => {
    if (isManager && user) {
      loadManagerStatus();
    }
  }, [isManager, user, loadManagerStatus]);

  const value = {
    // Estados
    sites,
    activeSite,
    locations,
    activeLocation,
    loading,
    error,
    managerStatus,
    isManager,
    needsSiteCreation: needsSiteCreation(),
    currentSubdomain: getCurrentSubdomain(),
    
    // Métodos de sites
    createSite,
    updateSite,
    getSite,
    changeActiveSite,
    getParentSites,
    getChildSites,
    loadSites,
    loadManagerStatus,
    
    // Métodos de locations
    createLocation,
    updateLocation,
    getLocation,
    loadLocationsBySite,
    getLocationsBySite,
    deleteLocation,
    changeActiveLocation,
    
    // Métodos de subdomain
    getCurrentSubdomain,
    hasSubdomainConfigured,
    isSubdomainMatching,
    shouldRedirectToSubdomain,
    buildSubdomainUrl,
    executeSubdomainRedirect,
    validateSubdomainAccess,
    
    // Utilidades
    clearError: () => setError(null),
    
    // Compatibilidad con nombres anteriores
    subdomains: sites,
    activeSubdomain: activeSite,
    // needsSubdomainCreation: needsSiteCreation(), // No se usa actualmente
    createSubdomain,
    updateSubdomain,
    getSubdomain,
    changeActiveSubdomain,
    getParentSubdomains,
    getChildSubdomains,
    loadSubdomains,
    loadLocationsBySubdomain
  };

  return (
    <SubdomainContext.Provider value={value}>
      {children}
    </SubdomainContext.Provider>
  );
};

// Mantener compatibilidad con el nombre anterior
export const SiteProvider = SubdomainProvider;
